import { assertNever } from "@impero-com/common/utils/obj";
import * as Sentry from "@sentry/react";

import { ControlProgramOverviewResourceDtoContent } from "api/generated/model";
import { buildControlProgramViewUrl } from "org/control/controlProgram/urlUtils";

import { useControlProgramsQuery } from "../queries/useControlProgramsQuery";

export interface ControlProgramOption {
    label: string;
    value: number;
    url: string;
}
const formatControlProgramsOptions = (
    controlPrograms: ControlProgramOverviewResourceDtoContent[]
): ControlProgramOption[] =>
    controlPrograms.map((controlProgram) => {
        const { id, name } = controlProgram.resource.controlProgram;
        return {
            label: name,
            value: id,
            url: buildControlProgramViewUrl(id),
        };
    });
interface UseControlProgramsSuccess {
    status: "success";
    data: ControlProgramOption[];
}
interface UseControlProgramsLoading {
    status: "loading";
    data: undefined;
}
type UseControlProgramOptions =
    | UseControlProgramsLoading
    | UseControlProgramsSuccess;

export type ControlProgramsActionFilter = "Edit" | "View";
// Primarily intended for inputs where an option can be selected
export const useControlProgramsOptions = (
    requiredAction: ControlProgramsActionFilter
): UseControlProgramOptions => {
    const {
        data: controlProgramOverviews,
        status,
        error,
    } = useControlProgramsQuery();

    switch (status) {
        case "success":
            const controlPrograms = controlProgramOverviews.content;
            const data = formatControlProgramsOptions(
                controlPrograms.filter((program) => {
                    switch (requiredAction) {
                        case "Edit":
                            return program.actions.edit.content;
                        case "View":
                            return program.actions.view;
                        default:
                            return assertNever(requiredAction);
                    }
                })
            );
            return {
                status: "success",
                data,
            };
        case "pending":
            return {
                status: "loading",
                data: undefined,
            };
        case "error":
            Sentry.captureException(error);
            console.error(error);
            throw error;
        default:
            assertNever(status);
    }
};
